import { styled, TypographyProps } from '@mui/material';

import { useNavigate } from 'react-router';

import { useMemo } from 'react';

import {
  Box,
  ButtonsContainer,
  Modal,
  Typography,
  useModalWrapperContext,
  Button,
} from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';

import { ScenarioRoutePaths } from '@app-velocity/modules/scenarios';

import { IncorrectFlowStructureImage } from '../IncorrectFlowStructureImage';

const StyledDescription = styled(Box)({
  textAlign: 'left',
});

const StyledTitle = styled(Typography)<
  TypographyProps & { component: React.ElementType }
>(({ theme }) => ({
  fontSize: 18,
  marginBottom: theme.spacing(2),
}));

const StyledText = styled(Typography)({
  color: 'var(--text-text-secondary-color)',
});

const ImageContainer = styled('div')({
  svg: {
    maxWidth: '100%',
  },
});

const isIncorrectFlowModalData = (
  data: unknown
): data is {
  nodeId: string;
  mode: 'authorized' | 'guest';
  workspaceId?: string | null;
  teamId?: string | null;
} => {
  const castedData = data as {
    nodeId: string;
    mode: 'authorized' | 'guest';
    workspaceId?: string | null;
    teamId?: string | null;
  };

  return (
    !!castedData.nodeId &&
    !!castedData.mode &&
    typeof castedData.nodeId === 'string'
  );
};

export const IncorrectFlowStructureModalContent = () => {
  const { modalData, openedModalId } = useModalWrapperContext();
  const { t } = useI18n();
  const { closeModal } = useModalWrapperContext();
  const navigate = useNavigate();

  const { nodeId, mode, workspaceId, teamId } = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isIncorrectFlowModalData(currentModalData)) {
      return currentModalData;
    }

    return { nodeId: null, mode: null, workspaceId: null, teamId: null };
  }, [modalData, openedModalId]);

  const goToChallengeCreation = () => {
    if (!nodeId || !mode) return;

    switch (mode) {
      case 'authorized': {
        if (!workspaceId || !teamId) break;

        navigate(
          ScenarioRoutePaths.configureNewChallenge(teamId, workspaceId, nodeId)
        );
        break;
      }

      case 'guest': {
        navigate(ScenarioRoutePaths.configureNewChallengeAsAGuest(nodeId));
        break;
      }
    }

    closeModal();
  };

  return (
    <Modal data-testid="speed-up-import-modal">
      <Modal.Content>
        <ImageContainer>
          <IncorrectFlowStructureImage />
        </ImageContainer>
        <StyledDescription>
          <StyledTitle variant="header" component="h2">
            {t('prototypes:speed_up_import.modal_title')}
          </StyledTitle>
          <StyledText variant="body2">
            {t('prototypes:speed_up_import.modal_description')}
          </StyledText>
        </StyledDescription>
        <ButtonsContainer direction="vertical">
          <Button
            variant="contained"
            color="primary"
            onClick={() => closeModal()}
          >
            {t('prototypes:speed_up_import.go_back')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={goToChallengeCreation}
          >
            {t('prototypes:speed_up_import.skip_and_proceed')}
          </Button>
        </ButtonsContainer>
      </Modal.Content>
    </Modal>
  );
};
